import type { OTCDealData } from '@src/types/OTC';
import {
  wsScanToMap,
  type CustomerMargin,
  type ICounterparty,
  type ISubAccountCollateralStatus,
  type Security,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { EMPTY, catchError, combineLatest, map, merge, scan, type Observable } from 'rxjs';
import {
  isCounterpartyMonitoringDeal,
  normalizeCustomerMarginRow,
  normalizeDealRow,
} from './counterpartyMonitoringRows';

interface CreateCounterpartyMonitoringDataObservableParams {
  customerMarginData: Observable<SubscriptionResponse<CustomerMargin>>;
  dealData: Observable<SubscriptionResponse<OTCDealData>>;
  subAccountCollateralStatusData: Observable<SubscriptionResponse<ISubAccountCollateralStatus>>;
  securitiesBySymbol: Observable<Map<string, Security>>;
  counterpartiesByName: Observable<Map<string, Pick<ICounterparty, 'Type'>>>;
}

function getSubAccountCollateralStatusPoolKey(status: ISubAccountCollateralStatus): string {
  return [
    status.OrgID,
    status.SubAccount,
    status.CollateralAsset,
    status.PrincipalAsset,
    status.CollateralProduct,
  ].join('|');
}

function buildSubAccountCollateralStatusByDealId(
  poolsById: Map<string, ISubAccountCollateralStatus>
): Map<string, ISubAccountCollateralStatus> {
  const subAccountCollateralStatusByDealId = new Map<string, ISubAccountCollateralStatus>();

  for (const status of poolsById.values()) {
    for (const memberDeal of status.MemberDeals ?? []) {
      subAccountCollateralStatusByDealId.set(memberDeal.DealID, status);
    }
  }

  return subAccountCollateralStatusByDealId;
}

export function createCounterpartyMonitoringDataObservable({
  customerMarginData,
  dealData,
  subAccountCollateralStatusData,
  securitiesBySymbol,
  counterpartiesByName,
}: CreateCounterpartyMonitoringDataObservableParams) {
  const customerRowsById = customerMarginData.pipe(
    wsScanToMap({
      getUniqueKey: row => row.rowID,
      newMapEachUpdate: false,
    }),
    map(customerRowsById => ({ customerRowsById }))
  );

  const dealRowsById = dealData.pipe(
    wsScanToMap({
      getUniqueKey: deal => deal.ID,
      shouldDelete: deal => !isCounterpartyMonitoringDeal(deal),
      newMapEachUpdate: false,
    }),
    map(dealRowsById => ({ dealRowsById }))
  );

  const subAccountCollateralStatusByDealId = subAccountCollateralStatusData.pipe(
    wsScanToMap({
      getUniqueKey: getSubAccountCollateralStatusPoolKey,
      newMapEachUpdate: false,
    }),
    map(poolsById => ({
      subAccountCollateralStatusByDealId: buildSubAccountCollateralStatusByDealId(poolsById),
    })),
    catchError(() => EMPTY)
  );

  // Retain the latest map from each independent subscription so any update re-normalizes the affected rows.
  const rowsById = merge(customerRowsById, dealRowsById, subAccountCollateralStatusByDealId).pipe(
    scan((rowsById, next) => ({ ...rowsById, ...next }), {
      customerRowsById: new Map<string, CustomerMargin>(),
      dealRowsById: new Map<string, OTCDealData>(),
      subAccountCollateralStatusByDealId: new Map<string, ISubAccountCollateralStatus>(),
    })
  );

  return combineLatest([rowsById, securitiesBySymbol, counterpartiesByName]).pipe(
    map(
      ([
        { customerRowsById, dealRowsById, subAccountCollateralStatusByDealId },
        securitiesBySymbol,
        counterpartiesByName,
      ]) => ({
        data: [
          ...[...customerRowsById.values()].map(row => normalizeCustomerMarginRow(row, counterpartiesByName)),
          ...[...dealRowsById.values()].map(
            deal =>
              normalizeDealRow(deal, securitiesBySymbol, counterpartiesByName, subAccountCollateralStatusByDealId)!
          ),
        ],
        initial: true,
      })
    )
  );
}
